import { Text, View, ScrollView, Pressable, RefreshControl, useWindowDimensions } from 'react-native';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Animated, { FadeInDown } from 'react-native-reanimated';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import Avatar from '@/components/People/Avatar';
import { markClassCompleted, unmarkClassCompleted, deleteClassInstance } from '@/data/training';
import { useNavigation, useRouter, useLocalSearchParams } from 'expo-router';
import { StyleSheet } from 'react-native-unistyles';
import Unicon from '@/components/General/Unicon';
import ContentLoader from '@/components/General/ContentLoader';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ActivityIndicator } from 'react-native-paper';
import { TabView } from 'react-native-tab-view';
import ReachTabBar from '@/components/General/ReachTabBar';
import usePlatform from '@/utils/hooks/usePlatform';
import DropdownMenu from '@/components/General/DropdownMenu';
import { showAlert } from '@/utils/showAlert';

export default function ClassInstanceDetail() {
    const { moduleID, instanceID } = useLocalSearchParams();
    const router = useRouter();
    const navigation = useNavigation();
    const queryClient = useQueryClient();
    const layout = useWindowDimensions();
    const { isNative } = usePlatform();

    const { data: loggedInUser } = Query.people.loggedInUser();
    const { data: instance, isLoading, isFetching } = Query.trainingClassInstances.byId({ id: instanceID });

    const [pendingIDs, setPendingIDs] = useState(new Set());
    const [tabIndex, setTabIndex] = useState(0);

    const invalidate = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.people.loggedInUser() });
        queryClient.invalidateQueries({ queryKey: ['trainingModules'] });
        queryClient.invalidateQueries({ queryKey: QueryKeys.trainingClassInstances.byId(instanceID) });
    }, [instanceID, moduleID]);

    useEffect(() => {
        if (instance) {
            const className = instance.Class?.name || 'Class Instance';
            navigation.setOptions({
                title: className,
                headerRight: loggedInUser?.can?.("Manage Training") ? () => (
                    <DropdownMenu
                        testID="HeaderMenu"
                        options={[
                            {
                                name: "Edit Instance",
                                onSelect: () => router.push({ pathname: `/training/${moduleID}/instance/create`, params: { id: instance.id, moduleID } })
                            },
                            {
                                name: "Delete Instance",
                                destructive: true,
                                onSelect: () => showAlert({
                                    title: "Delete this instance?",
                                    message: "This will permanently delete the scheduled instance and all attendance records.",
                                    confirmText: "Delete",
                                    cancelText: "Cancel",
                                    onConfirm: () => deleteInstanceMutation.mutate(instance.id)
                                })
                            }
                        ]}
                    />
                ) : undefined
            });
        }
    }, [instance, loggedInUser]);

    const markMutation = useMutation({
        mutationFn: markClassCompleted,
        onMutate: (vars) => setPendingIDs(prev => new Set(prev).add(vars.enrollmentID)),
        onSettled: (_, __, vars) => setPendingIDs(prev => { const s = new Set(prev); s.delete(vars.enrollmentID); return s; }),
        onSuccess: invalidate,
    });

    const unmarkMutation = useMutation({
        mutationFn: unmarkClassCompleted,
        onMutate: (vars) => setPendingIDs(prev => new Set(prev).add(vars.enrollmentID)),
        onSettled: (_, __, vars) => setPendingIDs(prev => { const s = new Set(prev); s.delete(vars.enrollmentID); return s; }),
        onSuccess: invalidate,
    });

    const deleteInstanceMutation = useMutation({
        mutationFn: deleteClassInstance,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['trainingModules'] });
            router.dismiss?.() || router.back();
        }
    });

    // Attendees: enrolled students who completed this class on THIS instance
    // Pending Completion: enrolled students who have NOT completed this classID on ANY instance
    const { attendees, pendingCompletion } = useMemo(() => {
        if (!instance) return { attendees: [], pendingCompletion: [] };

        const thisInstanceCompletions = new Map();
        (instance.Completions || []).forEach(c => {
            if (c.classInstanceID === instanceID) {
                thisInstanceCompletions.set(c.enrollmentID, c);
            }
        });

        const enrollments = instance.Class?.Module?.Enrollments?.filter(e => e.status === 'ENROLLED') || [];
        const attendees = [];
        const pendingCompletion = [];

        enrollments.forEach(enrollment => {
            const person = enrollment.Person;
            if (!person) return;

            const thisInstanceCompletion = thisInstanceCompletions.get(enrollment.id);
            if (thisInstanceCompletion) {
                attendees.push({ enrollment, person, completion: thisInstanceCompletion });
                return;
            }

            const hasCompletedClassAnywhere = (enrollment.Completions || []).some(c => c.classID === instance.classID);
            if (!hasCompletedClassAnywhere) {
                pendingCompletion.push({ enrollment, person });
            }
        });

        return { attendees, pendingCompletion };
    }, [instance, instanceID]);

    // Also show completions from people who may no longer be enrolled
    const otherCompletions = useMemo(() => {
        if (!instance) return [];
        const enrolledIDs = new Set((instance.Class?.Module?.Enrollments || []).map(e => e.id));
        return (instance.Completions || [])
            .filter(c => c.classInstanceID === instanceID && !enrolledIDs.has(c.enrollmentID))
            .map(c => ({ completion: c, person: c.Enrollment?.Person, enrollment: c.Enrollment }))
            .filter(e => e.person);
    }, [instance, instanceID]);

    const tabRoutes = useMemo(() => [
        { key: 'attendees', title: `Attendees (${attendees.length})` },
        { key: 'pending', title: `Pending Completion (${pendingCompletion.length})` },
    ], [attendees.length, pendingCompletion.length]);

    const handleToggle = (enrollment, isCompleted) => {
        if (!loggedInUser?.can?.("Manage Training Enrollments")) return;
        if (isCompleted) {
            unmarkMutation.mutate({ enrollmentID: enrollment.id, classID: instance.classID });
        } else {
            markMutation.mutate({ enrollmentID: enrollment.id, classID: instance.classID, markedByID: loggedInUser?.id, classInstanceID: instanceID });
        }
    };

    const markAllPresent = () => {
        pendingCompletion.forEach(({ enrollment }) => {
            markMutation.mutate({ enrollmentID: enrollment.id, classID: instance.classID, markedByID: loggedInUser?.id, classInstanceID: instanceID });
        });
    };

    const renderStudentCard = ({ enrollment, person, completion, isCompleted }) => (
        <Pressable
            key={enrollment.id}
            testID={`student-card-${enrollment.id}`}
            onPress={() => handleToggle(enrollment, isCompleted)}
            style={styles.studentCard}
        >
            <View style={styles.studentInfo}>
                <Pressable onPress={() => router.push("/people/" + person.id)}>
                    <Avatar profile={person} size={36} disableBorder />
                </Pressable>
                <View>
                    <Text style={styles.studentName}>{person.firstName} {person.lastName}</Text>
                    {completion?.completedAt && (
                        <Text style={styles.completedAt}>
                            Completed {new Date(completion.completedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                        </Text>
                    )}
                </View>
            </View>
            {pendingIDs.has(enrollment.id)
                ? <ActivityIndicator size={22} />
                : isCompleted
                    ? <Unicon name="checkmark-circle" size={22} colorVariant="success" />
                    : <Unicon name="ellipse-outline" size={22} colorVariant="textSecondary" />
            }
        </Pressable>
    );

    const renderAttendeesTab = () => (
        <ScrollView style={styles.tabContent} keyboardShouldPersistTaps="handled" refreshControl={<RefreshControl refreshing={isFetching} onRefresh={invalidate} />}>
            {attendees.length === 0 && otherCompletions.length === 0 && (
                <EmptyListPlaceholder icon="people-outline" message="No one attended this instance yet" />
            )}
            {attendees.map(entry => renderStudentCard({ ...entry, isCompleted: true }))}
            {otherCompletions.length > 0 && (
                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>Other ({otherCompletions.length})</Text>
                    {otherCompletions.map(({ completion, person }) => (
                        <View key={completion.id} style={styles.studentCard}>
                            <View style={styles.studentInfo}>
                                <Pressable onPress={() => router.push("/people/" + person.id)}>
                                    <Avatar profile={person} size={36} disableBorder />
                                </Pressable>
                                <View>
                                    <Text style={styles.studentName}>{person.firstName} {person.lastName}</Text>
                                    <Text style={styles.completedAt}>No longer enrolled</Text>
                                </View>
                            </View>
                            <Unicon name="checkmark-circle" size={22} colorVariant="success" />
                        </View>
                    ))}
                </View>
            )}
            <View style={{ height: 100 }} />
        </ScrollView>
    );

    const renderPendingTab = () => (
        <ScrollView style={styles.tabContent} keyboardShouldPersistTaps="handled" refreshControl={<RefreshControl refreshing={isFetching} onRefresh={invalidate}/>}>
            {loggedInUser?.can?.("Manage Training Enrollments") && pendingCompletion.length > 0 && (
                <View style={styles.actionRow}>
                    <Pressable testID="mark-all-present-btn" onPress={markAllPresent} style={styles.markAllButton}>
                        <Unicon name="checkmark-done-outline" size={16} colorVariant="onPrimary" />
                        <Text style={styles.markAllButtonText}>Mark All Present</Text>
                    </Pressable>
                </View>
            )}
            {pendingCompletion.length === 0 && (
                <EmptyListPlaceholder icon="checkmark-circle-outline" message="All enrolled students have completed this class" />
            )}
            {pendingCompletion.map(entry => renderStudentCard({ ...entry, completion: null, isCompleted: false }))}
            <View style={{ height: 100 }} />
        </ScrollView>
    );

    if (isLoading) return <ContentLoader />;
    if (!instance) return <EmptyListPlaceholder icon="school-outline" message="Instance not found" />;

    const scheduledDate = new Date(instance.scheduledAt);

    return (
        <View style={styles.container}>
            <Animated.View entering={FadeInDown.duration(300)} style={styles.inner}>
                {/* Instance Header */}
                <View style={styles.headerCard}>
                    <View style={styles.headerTop}>
                        <View style={styles.headerIcon}>
                            <Unicon name="calendar-outline" size={28} colorVariant="primary" />
                        </View>
                        <View style={styles.headerInfo}>
                            <View style={styles.headerTitleRow}>
                                <Text style={styles.headerTitle}>{instance.Class?.name}</Text>
                                {instance.Term?.name && (
                                    <View style={styles.termBadge}>
                                        <Text style={styles.termBadgeText}>{instance.Term.name}</Text>
                                    </View>
                                )}
                            </View>
                            <Text style={styles.headerDate}>
                                {scheduledDate.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                            </Text>
                        </View>
                    </View>
                    {instance.notes && <Text style={styles.headerNotes}>{instance.notes}</Text>}

                    <Pressable onPress={() => router.push(`/training/${moduleID}`)} style={styles.moduleLink}>
                        <Unicon name="school-outline" size={14} colorVariant="primary" />
                        <Text style={styles.moduleLinkText}>{instance.Class?.Module?.name || 'Module'}</Text>
                    </Pressable>

                    {instance.Teacher && (
                        <Pressable onPress={() => router.push(`/people/${instance.Teacher.id}`)} style={styles.teacherLink}>
                            <Avatar profile={instance.Teacher} size={24} disableBorder />
                            <Text style={styles.teacherLinkText}>{instance.Teacher.firstName} {instance.Teacher.lastName}</Text>
                        </Pressable>
                    )}

                    <View style={styles.quickStats}>
                        <View style={styles.quickStat}>
                            <Text style={styles.quickStatValue}>{attendees.length}</Text>
                            <Text style={styles.quickStatLabel}>Attended</Text>
                        </View>
                        <View style={styles.quickStat}>
                            <Text style={styles.quickStatValue}>{pendingCompletion.length}</Text>
                            <Text style={styles.quickStatLabel}>Pending</Text>
                        </View>
                    </View>
                </View>

                {/* TabView */}
                <TabView
                    swipeEnabled={isNative}
                    navigationState={{ index: tabIndex, routes: tabRoutes }}
                    onIndexChange={setTabIndex}
                    initialLayout={{ width: layout.width }}
                    renderTabBar={(props) => <ReachTabBar {...props} />}
                    lazy
                    renderScene={({ route }) => {
                        switch (route.key) {
                            case 'attendees': return renderAttendeesTab();
                            case 'pending': return renderPendingTab();
                            default: return null;
                        }
                    }}
                />
            </Animated.View>
        </View>
    );
}

const styles = StyleSheet.create(({ colors, fonts, spacing, typography }) => ({
    container: { flex: 1 },
    inner: { flex: 1, maxWidth: 800, width: '100%', alignSelf: 'center' },
    tabContent: { flex: 1, padding: spacing.md },

    // Header
    headerCard: { backgroundColor: colors.surface, borderRadius: 12, padding: spacing.lg, margin: spacing.md, marginBottom: 0 },
    headerTop: { flexDirection: 'row', alignItems: 'center', gap: spacing.md, marginBottom: spacing.sm },
    headerIcon: { width: 48, height: 48, borderRadius: 14, backgroundColor: colors.primaryContainer, alignItems: 'center', justifyContent: 'center' },
    headerInfo: { flex: 1 },
    headerTitleRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, flexWrap: 'wrap' },
    headerTitle: { fontSize: typography.h2, fontFamily: fonts.heading, color: colors.text },
    termBadge: { paddingVertical: 2, paddingHorizontal: 8, borderRadius: 6, backgroundColor: colors.secondaryContainer },
    termBadgeText: { fontSize: 11, fontFamily: fonts.bold, color: colors.secondary },
    headerDate: { fontSize: typography.caption, color: colors.textSecondary, marginTop: 2 },
    headerNotes: { fontSize: typography.body, color: colors.textSecondary, marginBottom: spacing.sm, fontStyle: 'italic' },
    moduleLink: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, marginBottom: spacing.sm, _web: { cursor: 'pointer', _hover: { opacity: 0.8 } } },
    moduleLinkText: { fontSize: 13, fontFamily: fonts.medium, color: colors.primary },
    teacherLink: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, marginBottom: spacing.md, _web: { cursor: 'pointer', _hover: { opacity: 0.8 } } },
    teacherLinkText: { fontSize: 13, fontFamily: fonts.medium, color: colors.text },
    teacherLinkLabel: { fontSize: 11, fontFamily: fonts.body, color: colors.textTertiary },

    quickStats: { flexDirection: 'row', gap: spacing.md, marginTop: spacing.xs },
    quickStat: { flex: 1, alignItems: 'center', backgroundColor: colors.background, borderRadius: 8, padding: spacing.sm },
    quickStatValue: { fontSize: 20, fontFamily: fonts.heading, color: colors.primary },
    quickStatLabel: { fontSize: 11, color: colors.textSecondary },

    // Action row
    actionRow: { marginBottom: spacing.md },
    markAllButton: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: spacing.xs, backgroundColor: colors.primary, paddingVertical: spacing.sm, paddingHorizontal: spacing.lg, borderRadius: 8, _web: { cursor: 'pointer', transition: 'all 0.15s ease', _hover: { opacity: 0.9 } } },
    markAllButtonText: { fontSize: typography.body, fontFamily: fonts.medium, color: colors.onPrimary },

    // Section
    section: { marginTop: spacing.md },
    sectionTitle: { fontSize: typography.body, fontFamily: fonts.heading, color: colors.text, marginBottom: spacing.sm },

    // Student card
    studentCard: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', backgroundColor: colors.surface, borderRadius: 10, padding: spacing.md, marginBottom: spacing.sm, _web: { transition: 'all 0.15s ease', cursor: 'pointer', _hover: { backgroundColor: colors.hover } } },
    studentInfo: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, flex: 1 },
    studentName: { fontSize: typography.body, fontFamily: fonts.medium, color: colors.text },
    completedAt: { fontSize: 11, color: colors.textSecondary, marginTop: 1 },
}));
